import { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axios';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToastContext } from '../contexts/ToastContext';

export default function ExportData({ data, filename = 'export', onExport, title, includeStats = false }) {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const toast = useToastContext();
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  const handleExport = async (format) => {
    console.log('Export clicked:', format, 'Data:', data?.length);
    setIsExporting(true);

    try {
      if (!data || data.length === 0) {
        toast.warning(t('no_data_export') || 'Tidak ada data untuk diekspor');
        return;
      }

      if (format === 'csv') {
        exportToCSV();
      } else if (format === 'json') {
        exportToJSON();
      } else if (format === 'pdf') {
        console.log('Starting PDF export...');
        exportToPDF();
      } else if (format === 'excel') {
        exportToExcel();
      }

      if (onExport) onExport(format);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error((t('export_failed') || 'Export gagal') + ': ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const exportToCSV = () => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]).filter(key =>
      key !== 'profile_photo' && key !== 'password' && key !== 'image'
    );

    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          return JSON.stringify(String(value));
        }).join(',')
      )
    ].join('\n');

    downloadFile(csvContent, `${filename}_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
  };

  const exportToJSON = () => {
    const cleanData = data.map(item => {
      const { profile_photo, password, image, ...cleanItem } = item;
      return cleanItem;
    });
    const jsonContent = JSON.stringify({
      exportInfo: {
        title: title || `${filename.charAt(0).toUpperCase() + filename.slice(1)} Export`,
        generatedAt: new Date().toISOString(),
        totalRecords: cleanData.length
      },
      data: cleanData
    }, null, 2);
    downloadFile(jsonContent, `${filename}_${new Date().toISOString().split('T')[0]}.json`, 'application/json');
  };

  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]).filter(key =>
      key !== 'profile_photo' && key !== 'password' && key !== 'image'
    );

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.join(',') + '\n';

    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvContent += values.join(',') + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = async () => {
    console.log('exportToPDF called');

    if (!data || data.length === 0) {
      console.error('No data for PDF');
      toast.warning(t('no_data_available') || 'Tidak ada data tersedia');
      return;
    }

    try {
      console.log('Fetching PDF data from backend');

      // Fetch formatted data from backend
      let pdfData;
      if (filename === 'properties') {
        const response = await api.get('/properties/export/pdf');
        pdfData = response.data.data;
      } else if (filename === 'users') {
        const response = await api.get('/users/export/pdf');
        pdfData = response.data.data;
      } else {
        // Fallback to current data
        pdfData = data;
      }

      console.log('Creating jsPDF instance');
      const doc = new jsPDF('l', 'mm', 'a4'); // landscape for more columns

      // Title
      doc.setFontSize(16);
      doc.text(title || `${filename.charAt(0).toUpperCase() + filename.slice(1)} Report`, 14, 15);

      // Date
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
      doc.text(`Total Records: ${pdfData.length}`, 14, 27);

      // Prepare table data
      const headers = Object.keys(pdfData[0] || {}).filter(key =>
        key !== 'profile_photo' && key !== 'password' && key !== 'image' &&
        key !== 'image_url' && key !== 'owner' && key !== 'description'
      );

      const rows = pdfData.map(item =>
        headers.map(header => {
          const value = item[header];
          if (value === null || value === undefined) return '-';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        })
      );

      autoTable(doc, {
        head: [headers.map(h => h.toUpperCase())],
        body: rows,
        startY: 32,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { top: 32 }
      });

      const fileName = `${filename}_${new Date().toISOString().split('T')[0]}.pdf`;
      console.log('Saving PDF:', fileName);
      doc.save(fileName);
      console.log('PDF saved successfully');

    } catch (error) {
      console.error('PDF export error:', error);
      toast.error((t('pdf_export_failed') || 'Export PDF gagal') + ': ' + error.message);
      throw error;
    }
  };

  const downloadFile = (content, fileName, contentType) => {
    try {
      const blob = new Blob([content], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        value={exportFormat}
        onChange={(e) => setExportFormat(e.target.value)}
        className={`px-3 py-2 border rounded-lg text-sm focus:ring-2 ${isDark ? 'border-gray-600 bg-gray-700 text-white focus:ring-gray-500 focus:border-gray-500' : 'border-gray-300 bg-white focus:ring-blue-500 focus:border-blue-500'}`}
        disabled={isExporting}
      >
        <option value="pdf">PDF</option>
        <option value="csv">CSV</option>
        <option value="excel">Excel</option>
        <option value="json">JSON</option>
      </select>

      <button
        onClick={() => handleExport(exportFormat)}
        disabled={isExporting || !data || data.length === 0}
        className={`px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2 ${isDark ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'}`}
      >
        {isExporting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            {exportFormat === 'pdf' ? (t('generating_pdf') || 'Generating PDF...') : (t('exporting') || 'Exporting...')}
          </>
        ) : (
          <>
            <i className="fas fa-download mr-1"></i> {t('export')} {exportFormat.toUpperCase()}
          </>
        )}
      </button>

      {data && data.length > 0 && (
        <span className={`text-xs ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {data.length} {t('records')}
        </span>
      )}
    </div>
  );
}